"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.doctorSlotBoardQuerySchema = exports.slotMapQuerySchema = exports.BOOKING_REQUEST_EXAMPLES = exports.emergencyAppointmentRequestSchema = exports.walkInAppointmentRequestSchema = exports.immediateIntakeRequestSchema = exports.packageAppointmentRequestSchema = exports.healthCheckAppointmentRequestSchema = exports.telemedicineAppointmentRequestSchema = exports.referralAppointmentRequestSchema = exports.corporateAppointmentRequestSchema = exports.bookWaivedRevisitRequestSchema = exports.createAppointmentRequestSchema = exports.appointmentBookingFieldsSchema = void 0;
/**
 * Request contracts for the 8 Booking endpoints. Field shapes mirror
 * each Booking Domain Service's own input interface exactly
 * (`backend/src/modules/appointment/booking/*.service.ts`, frozen,
 * Stage 5B.3) as orchestrated by `BookingApplicationService`
 * (frozen, Stage 5B.4) — this package does not import backend
 * source, so the shapes are re-declared here as the wire contract,
 * kept in lockstep by convention (the same posture
 * `registration.request.ts` already established for Patient Identity).
 */
const zod_1 = require("zod");
const appointment_common_request_1 = require("./appointment-common.request");
const baseBookingRequestSchema = zod_1.z.object({
    patientId: zod_1.z.string().uuid().optional(),
    /**
     * Book for someone who has not been registered yet — a phone booking with
     * only a name and a mobile number.
     *
     * Exactly one of `patientId` / `draftRegistrationId` must be supplied; the
     * database enforces it with a CHECK constraint, and the refinement below
     * turns that into a readable 4xx instead of a 500.
     */
    draftRegistrationId: zod_1.z.string().uuid().optional(),
    doctorId: zod_1.z.string().uuid(),
    branchId: zod_1.z.string().uuid(),
    departmentId: zod_1.z.string().uuid(),
    bookingChannelId: zod_1.z.string().uuid(),
    clinicalPurposeId: zod_1.z.string().uuid().optional(),
    consultationMode: zod_1.z.enum(['IN_PERSON', 'TELEMEDICINE', 'HOME_VISIT']).optional(),
    payerContext: zod_1.z.enum(['GENERAL', 'CORPORATE', 'INSURANCE', 'PACKAGE']).optional(),
    scheduledAt: zod_1.z.coerce.date(),
    /**
     * The doctor's numbered slot for that day (1..`Doctor.maxSlotsPerDay`).
     *
     * This number is the queue position, not a cosmetic label — slot 1 is seen
     * before slot 2. Optional because walk-in and emergency bookings legitimately
     * take no numbered slot; when supplied, the database's UNIQUE
     * (doctorId, slotDate, slotNumber) is what actually prevents two patients
     * landing on the same one.
     */
    slotNumber: zod_1.z.number().int().min(1).max(500).optional(),
    durationMinutes: zod_1.z.number().int().positive(),
    priorityTier: zod_1.z.number().int().optional(),
    finTariffId: zod_1.z.string().uuid().optional(),
    finPackageId: zod_1.z.string().uuid().optional(),
    finPaymentId: zod_1.z.string().uuid().optional(),
    patientReferralId: zod_1.z.string().uuid().optional(),
    reminder: appointment_common_request_1.bookingReminderRequestSchema.optional(),
    notification: appointment_common_request_1.notificationRequestSchema.optional(),
});
/**
 * The XOR is stated once, here, and reused by every booking variant — the
 * database's CHECK is the real guarantee, but a schema refinement turns a
 * constraint violation into a readable 422 instead of a 500.
 */
/**
 * The unrefined object, for callers that need to reshape it — a form omits the
 * fields it supplies from context, and `.omit()` does not exist on a refined
 * schema (`ZodEffects`). The refinement below is a wire-level rule; a form that
 * always supplies the subject from context does not need it.
 */
exports.appointmentBookingFieldsSchema = baseBookingRequestSchema;
const requireExactlyOneSubject = (schema) => schema.refine((value) => Boolean(value.patientId) !== Boolean(value.draftRegistrationId), { message: 'Supply exactly one of patientId or draftRegistrationId', path: ['patientId'] });
/**
 * Follow-up / Repeat, carried on the ordinary booking.
 *
 * ADDED ONLY TO THE STANDARD BOOKING, not to `baseBookingRequestSchema`.
 * Putting them on the base would make six other endpoints ACCEPT a revisit
 * they do not implement — the request would validate, the fields would be
 * dropped, and the appointment would come back looking booked with no link
 * to what it continues. An endpoint should reject what it cannot honour.
 *
 * `revisitWaived` IS DELIBERATELY ABSENT. Whether a revisit is free is the
 * server's decision (`RevisitAppointmentService.prepare()` owns the 7-day
 * same-doctor rule); a waiver the browser could assert is one no bill could
 * explain.
 *
 * `chargeAnyway` WAS HERE and is gone (2026-08-26). It was the operator's
 * answer to "the system says this is free; charge anyway?" — and the desk's
 * decision is that a follow-up is always free, which leaves an override with
 * only one direction to move a fee. Removing it from the contract is what stops
 * a browser from sending it and being quietly obeyed.
 *
 * `revisitType` NARROWS TO `FOLLOW_UP`. REPEAT — the same test performed again
 * — is gone from the product: no row in production ever carried it, and the
 * screen forced a choice between it and the thing the desk actually wanted. The
 * database enum keeps the value (a column may legally hold it); this is the
 * wire, and the wire no longer accepts it.
 */
const revisitBookingFieldsSchema = zod_1.z.object({
    parentAppointmentId: zod_1.z.string().uuid().optional(),
    revisitType: zod_1.z.enum(['FOLLOW_UP']).optional(),
});
/**
 * Both revisit fields or neither.
 *
 * A parent without a type would store a link the biller cannot read, and a type
 * without a parent would claim a continuation of nothing. Rejecting the half-supplied pair here turns it into a readable 422
 * rather than a row that is quietly wrong.
 */
const requireRevisitPairing = (schema) => schema.refine((value) => Boolean(value.parentAppointmentId) === Boolean(value.revisitType), {
    message: 'Supply parentAppointmentId and revisitType together, or neither',
    path: ['revisitType'],
});
exports.createAppointmentRequestSchema = requireRevisitPairing(requireExactlyOneSubject(baseBookingRequestSchema.merge(revisitBookingFieldsSchema)));
/**
 * A WAIVED SAME-DAY FOLLOW-UP — created and confirmed in ONE step, for the
 * EXACT slot picked (desk, 2026-09-11).
 *
 * WHY THIS IS ITS OWN ENDPOINT, NOT `createAppointmentRequestSchema` +
 * `PUT /:id/confirm`. That two-step path is what a live probe just proved
 * broken: `executeBooking()` deliberately writes NO slotNumber/slotDate for a
 * same-day booking (the 2026-09-09 same-day rule — a queue position is worth
 * nothing until it is real), and the plain `ConfirmAppointmentService` behind
 * `/confirm` only flips STATUS — it never allocates one. A future booking
 * never hits this because it keeps the slot it was created with; a same-day
 * one has none to keep, so a "waived, confirmed" follow-up came out with a
 * real status and an empty slot/number — invisible on the slot map, on every
 * dashboard, and to `appointments_active_slot_key`, the very guard that stops
 * a doctor being double-booked.
 *
 * `patientId` REQUIRED, no `draftRegistrationId` — same rule
 * `RevisitAppointmentService.prepare()` already enforces: a follow-up
 * continues one named patient's own history, and a draft has none.
 *
 * NO `revisitWaived` FIELD, same as every other revisit-carrying contract in
 * this file — the server decides it (`RevisitAppointmentService.prepare()`),
 * and REFUSES this call outright if the visit turns out to be chargeable.
 * This endpoint may only ever produce a free appointment.
 */
exports.bookWaivedRevisitRequestSchema = zod_1.z.object({
    patientId: zod_1.z.string().uuid(),
    doctorId: zod_1.z.string().uuid(),
    branchId: zod_1.z.string().uuid(),
    departmentId: zod_1.z.string().uuid(),
    bookingChannelId: zod_1.z.string().uuid(),
    consultationMode: zod_1.z.enum(['IN_PERSON', 'TELEMEDICINE', 'HOME_VISIT']).optional(),
    payerContext: zod_1.z.enum(['GENERAL', 'CORPORATE', 'INSURANCE', 'PACKAGE']).optional(),
    scheduledAt: zod_1.z.coerce.date(),
    /** The exact slot number picked. Never re-derived, never reassigned. */
    slotNumber: zod_1.z.number().int().min(1).max(500),
    durationMinutes: zod_1.z.number().int().positive(),
    parentAppointmentId: zod_1.z.string().uuid(),
    revisitType: zod_1.z.enum(['FOLLOW_UP']),
});
exports.corporateAppointmentRequestSchema = baseBookingRequestSchema
    .omit({ payerContext: true })
    .extend({ finTariffId: zod_1.z.string().uuid() });
exports.referralAppointmentRequestSchema = baseBookingRequestSchema.extend({
    patientReferralId: zod_1.z.string().uuid(),
});
exports.telemedicineAppointmentRequestSchema = baseBookingRequestSchema.omit({ consultationMode: true });
exports.healthCheckAppointmentRequestSchema = baseBookingRequestSchema.extend({
    clinicalPurposeId: zod_1.z.string().uuid(),
});
exports.packageAppointmentRequestSchema = baseBookingRequestSchema
    .omit({ payerContext: true })
    .extend({ finPackageId: zod_1.z.string().uuid() });
/** Shared by `walkInAppointment`/`emergencyAppointment` — identical shape (ADR-022: no `SlotHold`/`Appointment` involved for either), one schema, two named endpoint exports rather than a duplicated definition. */
exports.immediateIntakeRequestSchema = zod_1.z.object({
    patientId: zod_1.z.string().uuid(),
    branchId: zod_1.z.string().uuid(),
    departmentId: zod_1.z.string().uuid(),
    visitTypeId: zod_1.z.string().uuid(),
    doctorId: zod_1.z.string().uuid().optional(),
    notification: appointment_common_request_1.notificationRequestSchema.optional(),
});
exports.walkInAppointmentRequestSchema = exports.immediateIntakeRequestSchema;
exports.emergencyAppointmentRequestSchema = exports.immediateIntakeRequestSchema;
const UUID = '11111111-1111-1111-1111-111111111111';
exports.BOOKING_REQUEST_EXAMPLES = {
    createAppointment: {
        patientId: UUID,
        doctorId: '22222222-2222-2222-2222-222222222222',
        branchId: '33333333-3333-3333-3333-333333333333',
        departmentId: '44444444-4444-4444-4444-444444444444',
        bookingChannelId: '55555555-5555-5555-5555-555555555555',
        scheduledAt: new Date('2026-08-01T10:00:00Z'),
        durationMinutes: 15,
    },
    walkInAppointment: {
        patientId: UUID,
        branchId: '33333333-3333-3333-3333-333333333333',
        departmentId: '44444444-4444-4444-4444-444444444444',
        visitTypeId: '66666666-6666-6666-6666-666666666666',
    },
    emergencyAppointment: {
        patientId: UUID,
        branchId: '33333333-3333-3333-3333-333333333333',
        departmentId: '44444444-4444-4444-4444-444444444444',
        visitTypeId: '66666666-6666-6666-6666-666666666666',
    },
    corporateAppointment: {
        patientId: UUID,
        doctorId: '22222222-2222-2222-2222-222222222222',
        branchId: '33333333-3333-3333-3333-333333333333',
        departmentId: '44444444-4444-4444-4444-444444444444',
        bookingChannelId: '55555555-5555-5555-5555-555555555555',
        scheduledAt: new Date('2026-08-01T10:00:00Z'),
        durationMinutes: 15,
        finTariffId: '77777777-7777-7777-7777-777777777777',
    },
    referralAppointment: {
        patientId: UUID,
        doctorId: '22222222-2222-2222-2222-222222222222',
        branchId: '33333333-3333-3333-3333-333333333333',
        departmentId: '44444444-4444-4444-4444-444444444444',
        bookingChannelId: '55555555-5555-5555-5555-555555555555',
        scheduledAt: new Date('2026-08-01T10:00:00Z'),
        durationMinutes: 15,
        patientReferralId: '88888888-8888-8888-8888-888888888888',
    },
    telemedicineAppointment: {
        patientId: UUID,
        doctorId: '22222222-2222-2222-2222-222222222222',
        branchId: '33333333-3333-3333-3333-333333333333',
        departmentId: '44444444-4444-4444-4444-444444444444',
        bookingChannelId: '55555555-5555-5555-5555-555555555555',
        scheduledAt: new Date('2026-08-01T10:00:00Z'),
        durationMinutes: 15,
    },
    healthCheckAppointment: {
        patientId: UUID,
        doctorId: '22222222-2222-2222-2222-222222222222',
        branchId: '33333333-3333-3333-3333-333333333333',
        departmentId: '44444444-4444-4444-4444-444444444444',
        bookingChannelId: '55555555-5555-5555-5555-555555555555',
        scheduledAt: new Date('2026-08-01T10:00:00Z'),
        durationMinutes: 30,
        clinicalPurposeId: '99999999-9999-9999-9999-999999999999',
    },
    packageAppointment: {
        patientId: UUID,
        doctorId: '22222222-2222-2222-2222-222222222222',
        branchId: '33333333-3333-3333-3333-333333333333',
        departmentId: '44444444-4444-4444-4444-444444444444',
        bookingChannelId: '55555555-5555-5555-5555-555555555555',
        scheduledAt: new Date('2026-08-01T10:00:00Z'),
        durationMinutes: 15,
        finPackageId: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
    },
};
/**
 * Slot-map query — what the booking screen needs to paint slot buttons.
 *
 * `days` is capped at 7 deliberately: the front desk books a week ahead, and an
 * unbounded window would scan an arbitrary slice of the appointment table to
 * render buttons nobody is looking at.
 */
exports.slotMapQuerySchema = zod_1.z.object({
    doctorId: zod_1.z.string().uuid(),
    fromDate: zod_1.z.coerce.date(),
    days: zod_1.z.coerce.number().int().min(1).max(7).optional(),
});
/**
 * The doctor slot BOARD — one day, every appointment-enabled doctor.
 *
 * Desk, 2026-09-09: "user not able to see which doctor have how many slots."
 *
 * A DAY AND NOT A WINDOW, deliberately. The slot map takes `days` because the
 * booking screen paints a week of buttons for one doctor; this answers the
 * opposite question — who can see this patient TODAY — and a multi-day board
 * would be a grid of 38 doctors by 7 days that nobody can read at a counter.
 * Tomorrow is reached by changing the date, which is one control rather than a
 * second dimension.
 *
 * `date` is optional: a board with no date asked for means today, which is the
 * only sensible default for a screen opened with a patient standing there.
 */
exports.doctorSlotBoardQuerySchema = zod_1.z.object({
    date: zod_1.z.coerce.date().optional(),
    /** Narrows to one department — the desk's usual first cut. */
    departmentId: zod_1.z.string().uuid().optional(),
    /**
     * Narrows to ONE doctor — the `users` id, as every `doctorId` in this system
     * is (`Doctor.userId`, not `Doctor.id`).
     *
     * Added because the board's page must not open on everything (desk,
     * 2026-09-09: "user should ask for department or doctor then only show
     * searched values"). Both filters are still OPTIONAL here: the requirement is
     * the screen's, not the endpoint's, and an endpoint that refused an
     * unfiltered read would also refuse the whole-hospital board an administrator
     * can legitimately ask for. The read is bounded either way — one day, at most
     * every appointment-enabled doctor.
     */
    doctorId: zod_1.z.string().uuid().optional(),
});
//# sourceMappingURL=booking.request.js.map